import type {
  Datalayer,
  DatalayerInputEvent,
  DatalayerVariables,
} from '@analytics/models';
import { effect, inject, Injectable } from '@angular/core';
import { ConsentService } from '@core/consent';
import { PlatformService } from '@core/platform';

import { DatalayerEventProcessor } from './engine/event-processor.service';
import { DatalayerOutputManager } from './engine/output-manager.service';
import { DatalayerEventStream } from './engine/event-stream.service';

@Injectable({
  providedIn: 'root',
})
export class DatalayerService implements Datalayer {
  readonly #eventProcessor = inject(DatalayerEventProcessor);
  readonly #eventStream = inject(DatalayerEventStream);
  readonly #outputManager = inject(DatalayerOutputManager);
  readonly #platform = inject(PlatformService);
  readonly #consentService = inject(ConsentService);

  #variables: Partial<DatalayerVariables> = {};

  /**
   * Boots up the datalayer and starts measuring events.
   * When this function is called, captured events will be forwarded to all output channels
   * until the application is terminated.
   */
  init() {
    // Start processing incoming events, only on a client environment.
    if (this.#platform.isClient()) {
      this.#eventProcessor.startProcessingEvents(this);
    }
  }

  // Initialize the output dispatcher to configure output channels.
  readonly initOutputsOnConsent = effect(() => {
    if (this.#consentService.consentStatus() === true) {
      this.#outputManager.initOutputs();
    }
  });

  /**
   * Pushes the provided event into the datalayer for further event handling.
   * @param event object that satisfies the `DatalayerEvent` model.
   * @example
   * ```
   * this.#datalayer.pushEvent({
   *    event: 'button-click',
   *    info: {
   *      name: 'home-banner-cta-button',
   *    }
   * })
   * ```
   */
  pushEvent(event: DatalayerInputEvent) {
    this.#eventStream.eventTrack.next(event);
  }

  /**
   * Pushes a pageView into the datalayer for further event handling.
   * @param path a string representation of the page path, can contain params and fragments.
   * @example
   * ```
   * this.#datalayer.pushPageView('/products/01/details?v=single')
   * ```
   */
  pushPageView(path: string) {
    this.#eventStream.pageTrack.next(path);
  }

  /**
   * Pushes an exception into the datalayer for further event handling.
   * @note exceptions are equal to events in datastructure, but have their own datastream to enable specific output channels for
   * exceptions.
   * @param event object that satisfies the `DatalayerEvent` model.
   * @example
   * ```
   * this.#datalayer.pushException({
   *    event: 'api-call-failed',
   *    info: {
   *      endpoint: '/users/xxx/list-contracts',
   *      method: 'GET',
   *      statusCode: 404,
   *      errorBody: 'Failed to find user contracts'
   *    }
   * })
   * ```
   */
  pushException(event: DatalayerInputEvent) {
    this.#eventStream.eventTrack.next(event);
  }

  /**
   * Pushes or updates a variable in the datalayer.
   *
   * @param key - The key under which the variable will be stored.
   * @param value - The value to store in the datalayer. Can be of any type.
   * @param {Object} [options] - Optional settings to control how the variable is added or updated.
   * @param {boolean} [options.merge=false] - If true, merges the new value with the existing value. Arrays are concatenated, and objects are deep merged.
   * If the existing value and the new value are not of the same type (Array, Object) and the 'merge' flag is enabled, the stored
   * value will still be overwritten.
   *
   * @example
   * // Overwrite an existing variable
   * datalayer.pushVariable('user', { name: 'Alice' }, { overwrite: true });
   *
   * @example
   * // Merge with an existing object
   * datalayer.pushVariable('user', { roles: ['admin'] });
   * datalayer.pushVariable('user', { roles: ['editor'], id: 123 }, { merge: true });
   * // Result: { roles: ['admin', 'editor'], id: 123 }
   *
   * @example
   * // Default behavior (replace if exists, otherwise add)
   * datalayer.pushVariable('theme', 'dark');
   */
  pushVariable<K extends keyof DatalayerVariables>(
    key: K,
    value: DatalayerVariables[K],
    options: { merge?: boolean } = {}
  ): void {
    const { merge = false } = options;

    if (merge) {
      // Handle merging with existing value
      this.#variables[key] = this.#deepMergeAnalyticsVariable(
        this.#variables[key],
        value
      ) as DatalayerVariables[K];
    } else {
      // Default behavior: replace with new value
      this.#variables[key] = value;
    }
  }

  /**
   * Retrieves a variable by key.
   *
   * @param key The key of the variable to retrieve.
   * @returns The value of the variable associated with the given key, or `undefined` if the key does not exist.
   *
   * @example
   * // Retrieve a variable
   * const user = datalayer.getVariable('user');
   * console.log(user); // { roles: ['admin', 'editor'], id: 123 }
   */
  getVariable<K extends keyof DatalayerVariables>(key: K) {
    return this.#variables[key];
  }

  /**
   * Retrieves all variables in the datalayer.
   *
   * @returns A shallow copy of all stored variables as a key-value map.
   *
   * @example
   * // Retrieve all variables
   * const allVariables = datalayer.getVariables();
   * console.log(allVariables); // { user: { ... }, theme: 'dark' }
   */
  getVariables(): Partial<DatalayerVariables> {
    return { ...this.#variables };
  }

  #deepMergeAnalyticsVariable(target: any, source: any): any {
    if (Array.isArray(target) && Array.isArray(source)) {
      // Merge arrays by concatenating them
      return Array.from(new Set([...target, ...source]));
    }

    if (
      target &&
      source &&
      typeof target === 'object' &&
      typeof source === 'object' &&
      !Array.isArray(target) &&
      !Array.isArray(source)
    ) {
      // Merge objects recursively
      for (const key of Object.keys(source)) {
        target[key] = this.#deepMergeAnalyticsVariable(
          target[key],
          source[key]
        );
      }
      return target;
    }

    // For primitive types or incompatible types, return the source value
    return source;
  }
}
